import React, { useState } from "react";
import { useEditGroup } from "../hooks/useEditGroup";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";

const availableGroups = ["admin", "executive", "wholesale", "retail", "ecommerce"];

interface EditUserProps {
  username: string;
  currentGroups: string[];
  onClose: () => void;
}

const EditUser: React.FC<EditUserProps> = ({ username, currentGroups, onClose }) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>(currentGroups);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { mutate: editGroups, status } = useEditGroup();
  const isLoading = status === "pending";

  const handleGroupChange = (group: string) => {
    setSelectedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const groups_to_add = selectedGroups.filter((g) => !currentGroups.includes(g));
    const groups_to_remove = currentGroups.filter((g) => !selectedGroups.includes(g));
    editGroups(
      { username, groups_to_add, groups_to_remove },
      {
        onSuccess: () => {
          setMessage({ type: "success", text: "Groups updated successfully!" });
          setTimeout(onClose, 1000);
        },
        onError: (err: any) => {
          setMessage({ type: "error", text: err?.message || "Failed to update groups." });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {message && (
        <div
          className={`p-2 rounded text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium">Groups</label>
        <div className="space-y-2">
          {availableGroups.map((group) => (
            <div key={group} className="flex items-center space-x-2">
              <Checkbox
                id={group}
                checked={selectedGroups.includes(group)}
                onCheckedChange={() => handleGroupChange(group)}
              />
              <label htmlFor={group} className="text-sm">
                {group}
              </label>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default EditUser;